import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { fmt } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Send, Plus, X, Building2 } from "lucide-react";
import { POLinesTable, newPOLine, calcLine } from "@/components/po/POLinesTable";
import { PaymentTermsField } from "@/components/po/PaymentTermsField";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY_SHIP = { name: "", address: "", gstin: "", contact: "", state_code: "" };

export default function PurchaseOrderEditor() {
    const { id } = useParams();
    const [sp] = useSearchParams();
    const nav = useNavigate();
    const [vendors, setVendors] = useState([]);
    const [items, setItems] = useState([]);
    const [company, setCompany] = useState(null);
    const [f, setF] = useState({ vendor_id: "", po_date: today(), expected_delivery: "", reference: "", payment_terms: "",
                                 ship_to: EMPTY_SHIP, terms: [], notes: "", source_client_order_id: sp.get("from_order") || "" });
    const [lines, setLines] = useState([]);
    const [vendorRates, setVendorRates] = useState({});
    const [busy, setBusy] = useState(false);
    const [ready, setReady] = useState(false);
    const shipTouched = useRef(false);

    useEffect(() => {
        api.get("/vendors").then((r) => setVendors(r.data));
        api.get("/items", { params: { active_only: true } }).then((r) => setItems(r.data));
        api.get("/purchase-orders/defaults").then(async (r) => {
            setCompany(r.data.company);
            if (id) {
                const po = (await api.get(`/purchase-orders/${id}`)).data;
                shipTouched.current = true;
                setF({ vendor_id: po.vendor_id, po_date: po.po_date, expected_delivery: po.expected_delivery, reference: po.reference,
                       payment_terms: po.payment_terms, ship_to: po.ship_to, terms: po.terms, notes: po.notes,
                       source_client_order_id: po.source_client_order_id || "" });
                setLines(po.line_items.map((l) => ({ ...newPOLine(), ...l })));
            } else {
                setF((x) => ({ ...x, terms: r.data.terms }));
                const oid = sp.get("from_order");
                if (oid) {
                    const p = (await api.get(`/purchase-orders/prefill/client-order/${oid}`)).data;
                    setLines(p.line_items.map((l) => ({ ...newPOLine(), ...l })));
                    setF((x) => ({ ...x, ship_to: p.ship_to }));
                    shipTouched.current = true;
                    toast.info(`Prefilled ${p.line_items.length} items from the client order — enter vendor rates`, { id: "po-prefill" });
                } else setLines([newPOLine()]);
            }
            setReady(true);
        }).catch((e) => toast.error(e.response?.data?.detail || "Couldn't load"));
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!f.vendor_id) { setVendorRates({}); return; }
        api.get("/purchase-orders/vendor-rates", { params: { vendor_id: f.vendor_id } })
            .then((r) => setVendorRates(r.data.rates || {})).catch(() => setVendorRates({}));
    }, [f.vendor_id]);

    const pickVendor = async (vendor_id) => {
        setF((x) => ({ ...x, vendor_id }));
        if (!vendor_id || id) return;
        const d = (await api.get("/purchase-orders/defaults", { params: { vendor_id } })).data;
        setF((x) => ({
            ...x,
            ship_to: d.ship_to && !shipTouched.current ? d.ship_to : x.ship_to,
            payment_terms: x.payment_terms || d.payment_terms || "",
        }));
        if (d.ship_to && !shipTouched.current) toast.info("Ship To filled from this vendor's last PO — edit if needed");
    };
    const ship = (k, v) => { shipTouched.current = true; setF((x) => ({ ...x, ship_to: { ...x.ship_to, [k]: v } })); };
    const useOurs = () => { shipTouched.current = true; setF((x) => ({ ...x, ship_to: { name: company.name, address: company.address, gstin: company.gstin, contact: company.contact, state_code: company.state_code } })); };

    const totals = lines.reduce((t, l) => { const c = calcLine(l); return { taxable: t.taxable + c.amt, tax: t.tax + c.tax, total: t.total + c.total }; }, { taxable: 0, tax: 0, total: 0 });

    const save = async (markSent) => {
        setBusy(true);
        try {
            const body = { ...f, line_items: lines.filter((l) => l.name.trim()).map(({ key, ...l }) => ({
                ...l, qty: Number(l.qty), rate: Number(l.rate) || 0, tax_percent: Number(l.tax_percent) || 0 })) };
            const r = id ? await api.put(`/purchase-orders/${id}`, body) : await api.post("/purchase-orders", body);
            if (markSent && r.data.status === "draft") await api.post(`/purchase-orders/${r.data.id}/status`, { status: "sent" });
            toast.success(`${r.data.po_number} saved`);
            nav(`/purchase-orders/${r.data.id}`);
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(", ") : d || "Save failed");
        } finally { setBusy(false); }
    };

    if (!ready) return <div className="p-16 text-center"><Loader2 className="inline animate-spin text-slate-400"/></div>;
    return (
        <div data-testid="po-editor">
            <h1 className="text-3xl hdb-heading font-black text-slate-900">{id ? "Edit purchase order" : "New purchase order"}</h1>
            <p className="text-slate-500 mt-1 text-sm">Rates are before GST · PO number is assigned on first save (HDB/PO/…)</p>
            <div className="grid lg:grid-cols-3 gap-4 mt-6">
                <div className="hdb-card p-4">
                    <div className="hdb-label">Supplier</div>
                    <select className="hdb-input" value={f.vendor_id} onChange={(e) => pickVendor(e.target.value)} data-testid="po-vendor-select">
                        <option value="">Select vendor…</option>
                        {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                        <label><span className="hdb-label">PO date</span><input type="date" className="hdb-input hdb-mono" value={f.po_date} onChange={(e) => setF({ ...f, po_date: e.target.value })} data-testid="po-date"/></label>
                        <label><span className="hdb-label">Expected delivery</span><input type="date" className="hdb-input hdb-mono" value={f.expected_delivery} onChange={(e) => setF({ ...f, expected_delivery: e.target.value })} data-testid="po-expected"/></label>
                    </div>
                    <label className="block mt-2"><span className="hdb-label">Reference (optional)</span><input className="hdb-input" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="e.g. Client PO 1218" data-testid="po-reference"/></label>
                </div>
                <div className="hdb-card p-4 bg-slate-50" data-testid="po-bill-to">
                    <div className="hdb-label">Purchaser / Bill To</div>
                    <div className="font-bold text-slate-900 flex items-center gap-1.5"><Building2 size={14}/> {company?.name}</div>
                    <div className="text-xs text-slate-600 mt-1">{company?.address}</div>
                    <div className="text-xs mt-1">GSTIN <b className="hdb-mono">{company?.gstin}</b></div>
                    <div className="text-[10px] text-slate-400 mt-3">Always billed to Hardin Bachat — change in Settings.</div>
                </div>
                <div className="hdb-card p-4" data-testid="po-ship-to">
                    <div className="flex items-center justify-between"><div className="hdb-label">Consignee / Ship To *</div>
                        <button onClick={useOurs} className="text-[10px] font-bold text-emerald-700" data-testid="po-ship-ours">Use our address</button></div>
                    <input className="hdb-input" placeholder="Name" value={f.ship_to.name} onChange={(e) => ship("name", e.target.value)} data-testid="po-ship-name"/>
                    <textarea rows={2} className="hdb-input mt-1.5 text-sm" placeholder="Delivery address" value={f.ship_to.address} onChange={(e) => ship("address", e.target.value)} data-testid="po-ship-address"/>
                    <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                        <input className="hdb-input text-xs uppercase" placeholder="GSTIN" value={f.ship_to.gstin} onChange={(e) => ship("gstin", e.target.value.toUpperCase())} data-testid="po-ship-gstin"/>
                        <input className="hdb-input text-xs" placeholder="Contact" value={f.ship_to.contact} onChange={(e) => ship("contact", e.target.value)} data-testid="po-ship-contact"/>
                    </div>
                </div>
            </div>

            <div className="hdb-card p-4 mt-4"><POLinesTable lines={lines} setLines={setLines} items={items} vendorRates={vendorRates}/></div>

            <div className="grid lg:grid-cols-[1fr_320px] gap-4 mt-4">
                <div className="hdb-card p-4">
                    <PaymentTermsField value={f.payment_terms} onChange={(v) => setF({ ...f, payment_terms: v })}/>
                    <div className="hdb-label mt-4">2. Delivery location — printed from Ship To</div>
                    <div className="hdb-label mt-3">Other terms &amp; instructions</div>
                    <div className="space-y-1.5" data-testid="po-terms">
                        {f.terms.map((t, i) => (
                            <div key={i} className="flex gap-1.5 items-start">
                                <span className="text-xs text-slate-400 pt-2 w-5">{i + 3}.</span>
                                <input className="hdb-input text-sm" value={t} onChange={(e) => setF({ ...f, terms: f.terms.map((x, j) => (j === i ? e.target.value : x)) })} data-testid={`po-term-${i}`}/>
                                <button onClick={() => setF({ ...f, terms: f.terms.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-600 pt-2" data-testid={`po-term-remove-${i}`}><X size={14}/></button>
                            </div>
                        ))}
                    </div>
                    <button onClick={() => setF({ ...f, terms: [...f.terms, ""] })} className="hdb-btn hdb-btn-ghost text-xs mt-2" data-testid="po-term-add"><Plus size={12}/> Add term</button>
                    <label className="block mt-3"><span className="hdb-label">Remarks</span><input className="hdb-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="po-notes"/></label>
                </div>
                <div className="hdb-card p-4 h-fit lg:sticky lg:top-4" data-testid="po-summary">
                    <div className="flex justify-between text-sm"><span>Taxable value</span><b className="hdb-mono">{fmt(totals.taxable)}</b></div>
                    <div className="flex justify-between text-sm mt-1"><span>GST</span><b className="hdb-mono">{fmt(totals.tax)}</b></div>
                    <div className="flex justify-between text-lg font-black mt-2 pt-2 border-t border-slate-200"><span>Total PO value</span><span className="hdb-mono" data-testid="po-total">{fmt(totals.total)}</span></div>
                    <button disabled={busy} onClick={() => save(false)} className="hdb-btn hdb-btn-ghost w-full mt-4 justify-center" data-testid="po-save">
                        {busy ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>} Save draft</button>
                    <button disabled={busy} onClick={() => save(true)} className="hdb-btn hdb-btn-primary w-full mt-2 justify-center" data-testid="po-save-send">
                        <Send size={13}/> Save &amp; mark sent</button>
                </div>
            </div>
        </div>
    );
}
